"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
const roadmapSteps = [{
  title: "Discovery",
  description: "Understanding your unique business needs and identifying high-impact AI opportunities within your current workflows.",
  bullets: ["Comprehensive needs assessment", "Strategic goal setting", "Technical feasibility study"]
}, {
  title: "Audit",
  description: "Analyzing your current data infrastructure, security protocols, and system architecture to ensure AI readiness.",
  bullets: ["Data readiness and quality check", "System architecture review", "Security and compliance evaluation"]
}, {
  title: "Deployment",
  description: "Building, training, and seamlessly integrating custom AI solutions into your existing business environment.",
  bullets: ["Custom model training", "Seamless API integration", "Rigorous user acceptance testing"]
}, {
  title: "Monitoring",
  description: "Providing ongoing support, performance tracking, and model optimization to ensure sustained ROI and accuracy.",
  bullets: ["Real-time performance tracking", "Continuous model retraining", "24/7 technical support"]
}];
function TimelineItem({
  step,
  index,
  isLast
}: {
  step: typeof roadmapSteps[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className="relative flex w-full flex-col md:flex-row pb-24 last:pb-0">
      {}
      <div className="absolute left-6 top-0 bottom-0 w-px bg-primary/20 -translate-x-1/2 md:left-1/2" />

      {}
      <div className={`absolute left-6 top-0 w-px bg-primary -translate-x-1/2 md:left-1/2 transition-all duration-1000 ease-in-out origin-top ${isVisible ? "h-full" : "h-0"}`} />

      {}
      <div className={`absolute left-6 top-2 h-4 w-4 rounded-full border-2 border-primary bg-background -translate-x-1/2 md:left-1/2 transition-colors duration-500 delay-300 z-10 ${isVisible ? "bg-primary" : ""}`} />

      {}
      <div className={`ml-16 md:ml-0 flex w-full md:w-1/2 flex-col relative ${isEven ? "md:pr-16 md:items-end md:text-right" : "md:pl-16 md:ml-auto md:items-start md:text-left"}`}>
        <div className={`relative w-full max-w-md transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <span className={`absolute -top-12 text-[8rem] font-bold leading-none text-primary/5 select-none -z-10 ${isEven ? "md:-right-8 -left-4" : "-left-4"}`}>
            0{index + 1}
          </span>

          <h3 className="font-default text-2xl font-semibold text-foreground mb-3">
            {step.title}
          </h3>
          <p className="text-muted-foreground mb-6 leading-relaxed">
            {step.description}
          </p>

          <ul className={`flex flex-col gap-3 ${isEven ? "md:items-end" : "items-start"}`}>
            {step.bullets.map((bullet, i) => <li key={i} data-index={i} className={`flex items-start gap-3 text-sm text-foreground ${isEven ? "md:flex-row-reverse" : ""}`}>
                <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span className={isEven ? "md:text-right" : "text-left"}>
                  {bullet}
                </span>
              </li>)}
          </ul>
        </div>
      </div>
    </div>;
}
export default function TheImplementationRoadmap() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section id="the-implementation-roadmap" className="bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`mx-auto max-w-2xl text-center mb-20 transition-all duration-700 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-3xl md:text-4xl font-bold text-foreground mb-4">
            The Implementation Roadmap
          </h2>
          <p className="text-muted-foreground text-lg">
            A transparent, step-by-step process to transform your business operations with tailored AI solutions.
          </p>
        </div>

        <div className="relative mx-auto max-w-5xl">
          {roadmapSteps.map((step, index) => <TimelineItem key={index} step={step} index={index} isLast={index === roadmapSteps.length - 1} />)}
        </div>

        <div className="mt-20 flex justify-center">
          <Button size="lg" className="rounded-full px-8">
            Start Your AI Journey
          </Button>
        </div>
      </div>
    </section>;
}